import React from "react";

import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import Fade from "@mui/material/Fade";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { styled, ThemeProvider } from "@mui/material/styles";
import {
  PLAYTIME_PHASE,
  BUTTON_THEME,
  DISCUSS_PARTNER,
  TIMER_MODAL_SUBPHASE,
  SLIDE_ADDITIONAL_INFO,
  DIRECTION,
} from "../constants";
import TimerDisplay from "./TimerDisplay";

const PRIMARY_BTN = "timer-modal-primary-btn";
const SECONDARY_BTN = "timer-modal-secondary-btn";
const CONTINUE_BTN = "timer-modal-continue-btn";
const DISCUSS_CONTINUE_BTN = "timer-modal-discuss-continue-btn";

const BUTTON_STYLE = {
  [PRIMARY_BTN]: {
    width: "317px",
    height: "74px",
    fontSize: "28px",
  },
  [SECONDARY_BTN]: {
    width: "163px",
    height: "74px",
    fontSize: "28px",
  },
  [CONTINUE_BTN]: { width: "504px", height: "95px", fontSize: "46px" },
  [DISCUSS_CONTINUE_BTN]: {
    width: "504px",
    height: "95px",
    fontSize: "46px",
    marginTop: "10px",
  },
};

const { LOGIN, EXPLORE, KEEPGOING, FREEPLAY } = PLAYTIME_PHASE;
const { START, END, END_EARLY } = TIMER_MODAL_SUBPHASE;
const { PREV, NEXT } = DIRECTION;
const END_EARLY_NEXT = END_EARLY.concat("_", NEXT);
const END_EARLY_PREV = END_EARLY.concat("_", PREV);

const START_PLAY_DEFAULT_TEXT = {
  header: "Let's Play!",
  continueBtn: "Start",
};
const END_DEFAULT_TEXT = {
  header: "Time's Up!",
  continueBtn: "Continue",
};

const END_EARLY_NEXT_DEFAULT_TEXT = {
  header: "End timer early?",
  subheader: "You still have some time left...",
  continueBtn: "Next Slide",
  backBtn: "Close",
};

const END_EARLY_PREV_DEFAULT_TEXT = {
  header: "Go back a slide?",
  subheader: "Changing slides will end the timer early.",
  continueBtn: "Close",
  backBtn: "Previous Slide",
};

const END_FREEPLAY_DEFAULT_TEXT = {
  header: "Resume slides?",
  subheader: "This will pause student screens.",
};

const MODAL_TEXT = {
  [LOGIN]: {
    [START]: {
      header: "Login & Customize",
      continueBtn: "Start",
    },
    [END]: END_DEFAULT_TEXT,
    [END_EARLY_NEXT]: END_EARLY_NEXT_DEFAULT_TEXT,
    [END_EARLY_PREV]: END_EARLY_PREV_DEFAULT_TEXT,
  },
  [EXPLORE]: {
    [START]: START_PLAY_DEFAULT_TEXT,
    [END]: {
      header: "Poll Time!",
      subheader: "Time's up! It's time to vote...",
      continueBtn: "Go to Poll",
    },
    [END_EARLY_NEXT]: {
      ...END_EARLY_NEXT_DEFAULT_TEXT,
      continueBtn: "Go to Poll",
    },
    [END_EARLY_PREV]: END_EARLY_PREV_DEFAULT_TEXT,
  },
  [KEEPGOING]: {
    [START]: START_PLAY_DEFAULT_TEXT,
    [END]: {
      header: "Time's Up!",
      continueBtn: "Continue Story",
    },
    [END_EARLY_NEXT]: END_EARLY_NEXT_DEFAULT_TEXT,
    [END_EARLY_PREV]: END_EARLY_PREV_DEFAULT_TEXT,
  },
  [FREEPLAY]: {
    [END_EARLY_NEXT]: {
      ...END_FREEPLAY_DEFAULT_TEXT,
      continueBtn: "Next Slide",
      backBtn: "Close",
    },
    [END_EARLY_PREV]: {
      ...END_FREEPLAY_DEFAULT_TEXT,
      continueBtn: "Close",
      backBtn: "Previous Slide",
    },
  },
  [DISCUSS_PARTNER]: {
    [START]: {
      header: "Let's Pair Share!",
      subheader: "Talk with a partner.\nThen share out as a class.",
      continueBtn: "Start",
    },
    [END]: END_DEFAULT_TEXT,
    [END_EARLY_NEXT]: END_EARLY_NEXT_DEFAULT_TEXT,
    [END_EARLY_PREV]: END_EARLY_PREV_DEFAULT_TEXT,
  },
};
const DEFAULT_TIMEOUT = 500;

const TimerModal = ({
  open,
  handleClose,
  phase,
  subphase,
  time,
  nextSlideLabel,
  continueDisabled,
  endTimerEarlyDirection,
  setIsPaused,
  setEditingState,
  setIsGamePlaying,
  setTimerDuration,
  showTextDecoration,
}) => {
  return (
    <Modal disableRestoreFocus open={open} onClose={handleClose} disablePortal>
      <ModalContent
        open={open}
        handleClose={handleClose}
        phase={phase}
        subphase={subphase}
        time={time}
        nextSlideLabel={nextSlideLabel}
        continueDisabled={continueDisabled}
        endTimerEarlyDirection={endTimerEarlyDirection}
        setIsPaused={setIsPaused}
        setEditingState={setEditingState}
        setIsGamePlaying={setIsGamePlaying}
        setTimerDuration={setTimerDuration}
        showTextDecoration={showTextDecoration}
      />
    </Modal>
  );
};

export default TimerModal;

const ModalContent = ({
  open,
  handleClose,
  phase,
  subphase,
  time,
  nextSlideLabel,
  continueDisabled,
  endTimerEarlyDirection,
  setIsPaused,
  setEditingState,
  setIsGamePlaying,
  setTimerDuration,
  showTextDecoration,
}) => {
  const { header, subheader, continueBtn, backBtn } = getTexts(
    phase,
    subphase,
    nextSlideLabel,
    endTimerEarlyDirection
  );

  const isEndEarly = subphase === END_EARLY;
  const isStart = subphase === START;

  // rightBtn is also the single button in START and END
  const [leftBtn, rightBtn] =
    isEndEarly && endTimerEarlyDirection === PREV
      ? [
          { color: "primary", className: PRIMARY_BTN },
          { color: "secondary", className: SECONDARY_BTN },
        ]
      : [
          { color: "secondary", className: SECONDARY_BTN },
          {
            color: "primary",
            className: isEndEarly
              ? PRIMARY_BTN
              : isStart && phase === DISCUSS_PARTNER
              ? DISCUSS_CONTINUE_BTN
              : CONTINUE_BTN,
          },
        ];

  const timeoutMultiplier = continueDisabled ? 3 : 1;

  return (
    <Fade in={open} timeout={DEFAULT_TIMEOUT * timeoutMultiplier}>
      <div id="timer-modal">
        <div id="timer-modal-header">{header}</div>
        {subheader && <div id="timer-modal-subheader">{subheader}</div>}

        <div id="timer-modal-timer-text">
          <TimerDisplay
            time={time}
            setIsPaused={setIsPaused}
            setEditingState={setEditingState}
            setIsGamePlaying={setIsGamePlaying}
            setTimerDuration={setTimerDuration}
            timerType="modal"
            showTextDecoration={showTextDecoration}
            disableEditing={!isStart}
          />
        </div>

        <div id="timer-modal-buttons-container">
          <ThemeProvider theme={BUTTON_THEME}>
            {isEndEarly && (
              <StyledButton
                id="timer-modal-prev-btn"
                className={leftBtn.className}
                onClick={handleClose}
                variant="contained"
                color={leftBtn.color}
              >
                {backBtn ?? "Close"}
              </StyledButton>
            )}
            <StyledButton
              id="timer-modal-next-btn"
              className={rightBtn.className}
              onClick={handleClose}
              variant="contained"
              disabled={continueDisabled}
              color={rightBtn.color}
            >
              {isStart && (
                <PlayArrowIcon
                  sx={{ fontSize: "46px", marginRight: "16px " }}
                />
              )}
              {continueBtn}
              {!isStart && endTimerEarlyDirection !== PREV && (
                <PlayArrowIcon sx={{ fontSize: "30px", marginLeft: "16px" }} />
              )}
            </StyledButton>
          </ThemeProvider>
        </div>
      </div>
    </Fade>
  );
};

function getTexts(phase, displaySubphase, nextSlideLabel, dir) {
  const subphase =
    displaySubphase === END_EARLY && dir
      ? displaySubphase.concat("_", dir)
      : displaySubphase;
  if (
    phase === EXPLORE &&
    subphase !== START &&
    nextSlideLabel !== SLIDE_ADDITIONAL_INFO.polltime.label
  ) {
    // use text with continue story for subphase end (early)
    return MODAL_TEXT[KEEPGOING][subphase];
  } else {
    return MODAL_TEXT[phase][subphase];
  }
}

const StyledButton = styled(Button)((props) => {
  return {
    fontFamily: "Roboto Mono",
    fontWeight: 700,
    display: "flex-end",
    textTransform: " none",
    fontStyle: "normal",
    lineHeight: "normal",
    filter: props.continueDisabled ? "grayscale(1) brightness(80%)" : "none",
    transition: "filter 0.3s ease-in-out",
    ...BUTTON_STYLE[props.className],
  };
});
